<template>
  <div
    class="flex h-16 items-center"
    :class="{
      'flex-row-reverse gap-4 bg-outer-space-900 px-4': variant === 'goLive',
    }"
  >
    <div
      class="flex flex-grow items-center space-x-4"
      :class="{ 'min-w-0': variant === 'goLive' }"
    >
      <div class="flex-shrink-0">
        <Icon
          v-if="typeof config.icon === 'string'"
          size="1.5em"
          :name="config.icon"
          :class="config.iconClass"
        />
        <component
          :is="config.icon"
          v-else
          class="h-6 w-6"
          :class="config.iconClass"
        />
      </div>
      <div class="min-w-0 flex-grow">
        <div>{{ config.label }}</div>

        <div
          v-if="destination.platform_username"
          class="text-sm text-muted-foreground"
        >
          <template v-if="variant === 'default'">Connected to</template>
          <strong class="block w-full truncate">
            {{ destination.platform_username }}
          </strong>
        </div>
      </div>
    </div>
    <div class="flex flex-shrink-0 items-center space-x-2">
      <template v-if="!destination.is_connected">
        <Button
          variant="neutral"
          @click="() => multistream.connectPlatform(rstType)"
          ><span>Connect</span></Button
        >
      </template>
      <template v-else-if="!destination.has_permission">
        <DialogTikTokApply v-if="needsTikTokStreamingSetup">
          <UiDialogTrigger as-child>
            <UiSwitch size="lg" :variant="switchVariant" />
          </UiDialogTrigger>
        </DialogTikTokApply>
        <UiSwitch v-else size="lg" @click="doRepair" :variant="switchVariant" />
      </template>
      <template v-else>
        <UiSwitch
          :checked="destination.enabled"
          size="lg"
          :variant="switchVariant"
          :class="config.switchClass"
          @update:checked="
            (checked) => onDestinationToggle(destination, checked)
          "
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { updateRstUserProfile } from '~/lib/streamlabs-api';
import { PlatformTypeToRst } from '~/stores/multistream.store';

const multistream = useMultistreamStore();
const api = useApiStore();
const auth = useAuthStore();

const goLive = useGoLiveStore();

const props = defineProps<{
  destination: DestinationPlatform;
  variant?: 'default' | 'goLive';
}>();

const rstType = computed(() => {
  return PlatformTypeToRst[props.destination.platform_type];
});

const config = computed(() => {
  return multistream.platformConfigs[props.destination.platform_type];
});

const switchVariant = computed(() => {
  return props.variant === 'goLive' ? 'background' : 'default';
});

const onDestinationToggle = async (
  destination: Destination,
  checked: boolean,
) => {
  if (!api.multistream.user.profile.data) {
    return;
  }

  if (
    checked === true &&
    multistream.enabledDestinations.length >= 1 &&
    // 2 destinations and one is TikTok = OK, as long as grandfathered
    !(
      multistream.enabledDestinations.length === 1 &&
      (multistream.isTikTokEnabled || rstType.value === 'tiktok_account') &&
      multistream.isTikTokGrandfathered
    ) &&
    !auth.ensureUltra('multistream_toggle', {
      refl: 'obsplugin-multistream',
    })
  ) {
    return;
  }

  api.multistream.user.profile.data[rstType.value] = checked;

  await updateRstUserProfile(api.multistream.user.profile.data);
};

const needsTikTokStreamingSetup = computed(() => {
  return (
    props.destination.platform_type === 'tiktok' &&
    props.destination.no_permission_reason === 'platform_streaming'
  );
});
const doRepair = async () => {
  await multistream.connectPlatform(rstType.value);
};
</script>
